// apps/smart-shikka-web/src/pages/chat/ApprovalCard.tsx
// V2 §Q — inline approval consume card inside the normal Smart Shikka chat.
// When a privileged turn returns `approvalRequest`, this card binds the same
// actor/session/action digest and consumes the canonical `/api/ceo/approve`
// route — no separate CEO surface. The card is purely presentational; the
// decision state machine and service call live in Chat.tsx.
import { useEffect, useState } from "react";
import {
  productText,
  useBrandColors,
  type CeoApprovalRequest,
  type ProductLocale,
} from "@smart-shikka/react";

export type ApprovalDecisionState =
  | "pending"
  | "submitting"
  | "consumed"
  | "denied"
  | "expired"
  | "replayed"
  | "failed";

export interface ApprovalConsumeCardProps {
  readonly locale: ProductLocale;
  readonly request: CeoApprovalRequest;
  readonly state: ApprovalDecisionState;
  readonly errorCode?: string | undefined;
  readonly onApprove: () => void;
  readonly onDeny: () => void;
  readonly onExpire: () => void;
}

function levelLabel(level: CeoApprovalRequest["permissionLevel"], locale: ProductLocale): string {
  switch (level) {
    case "session_grant":
      return locale === "hi" ? "सत्र अनुमति" : "Session grant";
    case "fresh_approval":
      return locale === "hi" ? "नई अनुमति" : "Fresh approval";
    default:
      return locale === "hi" ? "स्वतः" : "Auto";
  }
}

export function ApprovalConsumeCard(props: ApprovalConsumeCardProps): React.JSX.Element {
  const C = useBrandColors();
  const { request, state, locale } = props;
  const [, setTick] = useState(0);

  // Tick every second so the expiry label and the pending→expired transition
  // track the real clock. The parent marks the card expired via onExpire.
  useEffect(() => {
    if (state !== "pending" || request.expiresAt === undefined) return;
    const t = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [state, request.expiresAt]);

  const remainingMs =
    request.expiresAt !== undefined ? request.expiresAt - Date.now() : undefined;
  const expiredLocally = remainingMs !== undefined && remainingMs <= 0;
  useEffect(() => {
    if (expiredLocally && state === "pending") props.onExpire();
  }, [expiredLocally, state, props]);

  const terminal = state !== "pending" && state !== "submitting";
  const statusLine =
    state === "consumed"
      ? productText("approvalApprovedDone", locale)
      : state === "denied"
        ? productText("approvalDeniedDone", locale)
        : state === "expired"
          ? productText("approvalExpiredCard", locale)
          : state === "replayed"
            ? productText("approvalAlreadyUsed", locale)
            : state === "failed"
              ? productText("approvalApplyFailed", locale)
              : undefined;
  const statusColor =
    state === "consumed"
      ? C.success
      : state === "denied"
        ? C.muted
        : terminal
          ? C.destructive
          : C.muted;

  return (
    <div
      data-testid="approval-consume-card"
      role="group"
      aria-label={productText("approvalNeeded", locale)}
      style={{
        margin: "8px 16px 4px",
        border: `1px solid ${terminal ? C.border : C.primary}`,
        borderRadius: 12,
        padding: "10px 12px",
        background: C.card,
        fontFamily: C.fontSans,
        fontSize: 12.5,
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontWeight: 700, color: C.fg }}>
          {productText("approvalNeeded", locale)}
        </span>
        <span
          style={{
            fontSize: 10.5,
            padding: "1px 7px",
            borderRadius: 999,
            border: `1px solid ${C.border}`,
            color: C.muted,
          }}
        >
          {levelLabel(request.permissionLevel, locale)}
        </span>
      </div>
      <div style={{ color: C.fg, fontWeight: 600 }}>{request.actionSummary}</div>
      {request.prompt ? (
        <div style={{ color: C.muted, lineHeight: 1.45 }}>{request.prompt}</div>
      ) : null}
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        {remainingMs !== undefined && !expiredLocally ? (
          <span style={{ color: C.muted, fontSize: 11 }}>
            {productText("approvalExpiryPrefix", locale)}: {Math.max(1, Math.ceil(remainingMs / 60000))} min
          </span>
        ) : null}
        {statusLine ? (
          <span style={{ color: statusColor, fontSize: 11.5, fontWeight: 600 }}>{statusLine}</span>
        ) : null}
        {props.errorCode && state === "failed" ? (
          <span style={{ color: C.muted, fontSize: 11 }}>{props.errorCode}</span>
        ) : null}
        <div style={{ flex: 1 }} />
        {state === "pending" && !expiredLocally ? (
          <>
            <button
              type="button"
              data-testid="approval-approve"
              onClick={props.onApprove}
              style={{
                border: "none",
                borderRadius: 8,
                padding: "6px 14px",
                background: C.primary,
                color: "#fff",
                fontWeight: 700,
                fontSize: 12,
                cursor: "pointer",
              }}
            >
              {productText("approvalApprove", locale)}
            </button>
            <button
              type="button"
              data-testid="approval-deny"
              onClick={props.onDeny}
              style={{
                border: `1px solid ${C.border}`,
                borderRadius: 8,
                padding: "6px 14px",
                background: "transparent",
                color: C.fg,
                fontWeight: 600,
                fontSize: 12,
                cursor: "pointer",
              }}
            >
              {productText("approvalDeny", locale)}
            </button>
          </>
        ) : state === "submitting" ? (
          <span style={{ color: C.muted, fontSize: 11.5 }}>
            {productText("approvalApplying", locale)}
          </span>
        ) : null}
      </div>
    </div>
  );
}
